import { useNavigate, useSearchParams } from "react-router-dom";
import { Heading } from "../../../../components/common/typography";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { SimpleTable } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { useProductRequestsGroupedByUser } from "../../hooks/useProductRequestsGroupedByUser";
import { useProductReviewStats } from "../../hooks/useProductReviewStats";
import { usePaginationSortingUrlSync } from "../../../pagination/hooks/usePaginationSortingUrlSync";
import { useSearchFiltersUrlSync } from "../../../pagination/hooks/useSearchFiltersUrlSync";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { ProductRequestQueryDto } from "../../../../api";
import { ProductRequestQueryDtoRequestStatusEnum } from "../../../../api";
import { buildAdminProductReviewColumns } from "../components/admin-product-review-columns";

type StatusFilter = "all" | "APPROVED" | "REJECTED" | "PENDING";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "PENDING", label: "Pending" },
];
const VALID_STATUSES = new Set<StatusFilter>(STATUS_OPTIONS.map((o) => o.value));

export function AdminProductRequestsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get("status") as StatusFilter | null;
  const status: StatusFilter =
    statusParam && VALID_STATUSES.has(statusParam) ? statusParam : "all";

  const setStatus = (next: StatusFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("status");
    else params.set("status", next);
    params.delete("page");
    setSearchParams(params, { replace: false });
  };

  const { searchValue, setSearchValue, baseQuery } =
    useSearchFiltersUrlSync<ProductRequestQueryDto>({
      initialFilters: {} as Record<string, unknown>,
      enableSearch: true,
    });

  const { pagination } = usePaginationSortingUrlSync({ defaultPageSize: 10 });

  const { data, isLoading } = useProductRequestsGroupedByUser({
    ...baseQuery,
    requestStatus: status === "all" ? undefined : status as ProductRequestQueryDtoRequestStatusEnum,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });

  const stats = useProductReviewStats();
  const rows = data?.data ?? [];
  const pageCount = data?.meta?.pageCount ?? 0;

  const columns = buildAdminProductReviewColumns((userId) =>
    navigate(`${AppRoutes.ADMIN_PRODUCT_REQUESTS}/${userId}`),
  );

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <Heading variant="h2" tone="dark">Product review</Heading>

      <div className="grid grid-cols-3 gap-4 max-md:gap-2 max-md:[&>*]:px-3">
        <AccentStatCard
          accent="green"
          label="Approved"
          value={(stats.approved ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="cyan"
          label="Pending approval"
          value={(stats.pending ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="amber"
          label="Rejected"
          value={(stats.rejected ?? 0).toLocaleString()}
        />
      </div>

      <Heading variant="h3" tone="dark">All products by supplier</Heading>

      <div className="flex items-center gap-3 max-md:flex-wrap">
        <div className="w-4/5 max-md:w-full">
          <SearchBar
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search"
            wrapperClassName=""
          />
        </div>
        <SegmentedControl
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          className="!py-1 [&_button]:!py-1"
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Loading product requests…" lines={6} />
      ) : (
        <SimpleTable
          columns={columns}
          data={rows}
          keyExtractor={(r) => r.userId}
          onRowClick={(r) => navigate(`${AppRoutes.ADMIN_PRODUCT_REQUESTS}/${r.userId}`)}
          footer={pageCount > 1 ? <PaginationFooter pageCount={pageCount} /> : undefined}
        />
      )}
    </div>
  );
}

function PaginationFooter({ pageCount }: { pageCount: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") ?? "1") || 1;
  return (
    <div className="flex justify-center">
      <Pagination
        page={page}
        pageCount={pageCount}
        onPageChange={(next) => {
          const params = new URLSearchParams(searchParams);
          params.set("page", String(next));
          setSearchParams(params, { replace: true });
        }}
      />
    </div>
  );
}
